import { serializeAttachmentUpload } from '@services/attachmentChanges/attachmentUploadOrigins';
import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IDocumentTable, IForms } from '@models/Forms/IForms';
import { IFormData } from '@models/IFormData';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { IDocumentResult } from '@models/document/IDocumentResult';
import {
    IRequestAdditionalApprovalModel,
    IRequestAdditionalApprovalSend,
} from '@models/document/activityExecutors/IRequestAdditionalApprovalModel';
import { IActivityActionModel } from '@models/document/activityExecutors/IActivityActionModel';
import { IPartsSign } from '@models/attaches/IAttachViewSignModel';
import { IActionResultModel } from '@models/document/actionsExecution/IActionResultModel';
import { IAbookOptions } from '@models/Forms/IAbookOptions';
import { IResolveExceptionModel } from '@models/document/activityExecutors/IResolveExceptionModel';
import { ActFinishType } from '@/types/ActFinishType';
import { IBatchEditingTableData } from '@models/actions/IBatchEditingTableData';
import { createUploadProgressHandler, UploadProgressHandler } from '@utils/uploadProgress';
import { withGroupRun } from './groupOperationRun';

export class ActivityExecutionService {
    getRequestAdditionalApprovalModel(
        actionKey: string,
        activityId: string,
    ): Promise<AxiosResponse<IRequestAdditionalApprovalModel>> {
        return $api.get<IRequestAdditionalApprovalModel>(
            `activityActions/requestAdditionalApproval/${activityId}/${actionKey}`,
        );
    }

    sendRequestAdditionalApproval(
        actionKey: string,
        activityId: string,
        data: IRequestAdditionalApprovalSend,
    ): Promise<AxiosResponse> {
        return $api.post(`activityActions/requestAdditionalApproval/${activityId}/${actionKey}`, data);
    }

    takeToWork(actionKey: string, actId: string, comment?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('comment', comment as string);
        const url = `activityActions/takeToWork/${actId}/${actionKey}`;
        return withGroupRun(actionKey, (params) =>
            params ? $api.post(url, formData, { params }) : $api.post(url, formData),
        );
    }

    getActivityActionModel(actionKey: string, activityId: string): Promise<AxiosResponse<IActivityActionModel>> {
        return $api.get<IActivityActionModel>(`activityActions/activityAction/${activityId}/${actionKey}`);
    }

    //согласовать, согласовать с замечаниями или отклонить
    activityActionWithSign(
        actionKey: string,
        actId: string,
        signs: IPartsSign[],
        actFinishType: ActFinishType,
        comment?: string,
    ): Promise<AxiosResponse<IActionResultModel>> {
        let data = {
            comment: comment,
            signs: signs,
            actFinishType: actFinishType,
        };
        return $api.post(`activityActions/activityActionWithSign/${actId}/${actionKey}`, data);
    }

    activityAction(
        actionKey: string,
        actId: string,
        actFinishType: ActFinishType,
        comment?: string,
    ): Promise<AxiosResponse<IActionResultModel>> {
        let data = {
            comment: comment,
            actFinishType: actFinishType,
        };
        const url = `activityActions/activityAction/${actId}/${actionKey}`;
        return withGroupRun(actionKey, (params) => (params ? $api.post(url, data, { params }) : $api.post(url, data)));
    }

    // Сохранить набор полей в документах
    async saveBatchEditingData(
        documentIds: string,
        actionKey: string,
        activityIds: string,
        data: IFormData,
        files?: ISelectedFiles,
        onUploadProgress?: UploadProgressHandler,
    ): Promise<AxiosResponse<IDocumentResult>> {
        const formData = new FormData();
        formData.append('documentIds', documentIds);
        formData.append('actionKey', actionKey);
        formData.append('activityIds', activityIds);
        formData.append('data', JSON.stringify(data));

        if (files) {
            files.attachProperties.map((prop) => formData.append('attachProps', serializeAttachmentUpload(prop)));
            files.files.map((f) => formData.append('attachFiles', f));
        }

        return $api.post(`/activityActions/saveBatchEditingData`, formData, {
            suppressErrorNotification: true,
            onUploadProgress: createUploadProgressHandler(files?.files, onUploadProgress),
        });
    }

    activityComplete(actionKey: string, actId: string, comment?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('comment', comment ?? '');

        const url = `activityActions/activityComplete/${actionKey}/${actId}`;
        return withGroupRun(actionKey, (params) =>
            params ? $api.post(url, formData, { params }) : $api.post(url, formData),
        );
    }

    // Возвращает модель для формы
    async getFormBuilderScheme(actionKey: string): Promise<AxiosResponse<IForms>> {
        return $api.get<IForms>(`/activityActions/getFormBuilderScheme/${actionKey}`);
    }

    async getReplacePerformerModel(actionKey: string, activityId: string): Promise<AxiosResponse<IAbookOptions>> {
        return $api.get<IAbookOptions>(`activityActions/replacePerformer/${activityId}/${actionKey}`);
    }

    async saveReplacePerformer(actionKey: string, activityId: string, performerKey: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('performerKey', performerKey);
        return $api.post(`activityActions/replacePerformer/${activityId}/${actionKey}`, formData);
    }

    async saveRollbackPerformer(actionKey: string, activityId: string): Promise<AxiosResponse> {
        return $api.post(`activityActions/rollbackPerformer/${activityId}/${actionKey}`);
    }

    async getDelegateModel(actionKey: string, activityId: string): Promise<AxiosResponse<IAbookOptions>> {
        return $api.get<IAbookOptions>(`activityActions/delegate/${activityId}/${actionKey}`);
    }

    async saveDelegate(
        actionKey: string,
        activityId: string,
        performerKey: string,
        comment?: string,
    ): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('performerKey', performerKey);
        formData.append('comment', comment ?? '');
        return $api.post(`activityActions/delegate/${activityId}/${actionKey}`, formData);
    }

    async saveRollbackDelegate(actionKey: string, activityId: string, comment?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('comment', comment ?? '');
        return $api.post(`activityActions/rollbackDelegate/${activityId}/${actionKey}`, formData);
    }

    async getChangePerformerModel(actionKey: string, activityId: string): Promise<AxiosResponse<IAbookOptions>> {
        return $api.get<IAbookOptions>(`activityActions/changePerformer/${activityId}/${actionKey}`);
    }

    async saveChangePerformer(
        actionKey: string,
        activityId: string,
        performerKey: string,
        reason?: string,
    ): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('performerKey', performerKey);
        formData.append('reason', reason ?? '');
        return $api.post(`activityActions/changePerformer/${activityId}/${actionKey}`, formData);
    }

    async getResolveExceptionModel(
        actionKey: string,
        activityId: string,
    ): Promise<AxiosResponse<IResolveExceptionModel>> {
        return $api.get<IResolveExceptionModel>(`activityActions/resolveException/${activityId}/${actionKey}`);
    }

    async saveResolveException(actionKey: string, activityId: string, templateId: string): Promise<AxiosResponse> {
        return $api.post(`activityActions/resolveException/${activityId}/${actionKey}/${templateId}`);
    }

    // Возвращает модель таблицы для BatchEditingTable
    async getBatchEditingTableScheme(actionKey: string, activityId: string): Promise<AxiosResponse<IDocumentTable>> {
        return $api.get<IDocumentTable>(`/activityActions/batchEditingTableScheme/${activityId}/${actionKey}`);
    }

    // Возвращает данные таблицы для BatchEditingTable
    async getBatchEditingTableData(
        actionKey: string,
        activityId: string,
        docId: string,
    ): Promise<AxiosResponse<IBatchEditingTableData>> {
        return $api.get<IBatchEditingTableData>(
            `/activityActions/batchEditingTableData/${activityId}/${docId}/${actionKey}`,
        );
    }

    // Сохранить набор полей в документах
    async saveBatchEditingTableData(
        activityId: string,
        documentId: string,
        actionKey: string,
        data: IFormData,
    ): Promise<AxiosResponse<IDocumentResult>> {
        const formData = new FormData();
        formData.append('activityId', activityId);
        formData.append('docId', documentId);
        formData.append('actionKey', actionKey);
        formData.append('data', JSON.stringify(data));

        return $api.post(`/activityActions/saveBatchEditingTableData`, formData, {
            suppressErrorNotification: true,
        });
    }
}
